/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import Checkbox from '@mui/material/Checkbox'
import { isEqual } from 'lodash'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import { ReactComponent as ArrowUpIcon } from 'assets/icons/arrow-up.svg'
import { OrderDirection } from '__generatedTypes__/globalTypes'
import Loader from '../Loader'
import * as classes from './styles'

const DND_ANIMATION = 200

export type Order = {
  field: string
  direction: OrderDirection
} | null

const getNextOrder = (field: string, currentOrder?: Order): Order => {
  if (currentOrder?.field !== field) {
    return {
      field,
      direction: OrderDirection.ASC,
    }
  }

  if (currentOrder.direction === OrderDirection.ASC) {
    return {
      field,
      direction: OrderDirection.DESC,
    }
  }

  return null
}
//TODO should deprecate flex and use className
export type TableProps<Item> = {
  columns: Array<{
    title?: string | React.ReactElement
    flex?: number | string
    renderCell?: (item: Item, rowIndex: number) => string | React.ReactElement
    orderField?: string
    className?: string
  }>
  items: Item[]
  activeItem?: Item
  onItemClick?: (item: Item) => void
  order?: Order
  onOrder?: (order: Order) => void
  isSortable?: boolean
  onSort?: (items: Item[]) => void
  selectedItems?: Item[]
  isSelectable?: boolean
  onSelect?: (items: Item[]) => void
  wrapperMinWidth?: number
  wrapperPadding?: number
  hideHeader?: boolean
  isNotLoaded?: boolean
  className?: string
  wrapperClassName?: string
}

export function Table<Item extends { id: string }>({
  columns,
  items,
  activeItem,
  onItemClick,
  order = null,
  onOrder,
  isSortable = false,
  onSort,
  selectedItems = [],
  isSelectable = false,
  onSelect,
  wrapperMinWidth = 500,
  wrapperPadding = 24,
  hideHeader,
  isNotLoaded,
  className,
  wrapperClassName,
  ...props
}: TableProps<Item>) {
  const sortedItems = items.map((item) => ({ id: item.id, item }))
  const selectedItemsIds = selectedItems.map((item) => item.id)
  const isAllSelected = !!selectedItems?.length && selectedItems.length === items.length
  const [isScrollable, setIsScrollable] = useState(false)
  const scrollableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateScrollable = () => {
      if (scrollableRef.current) {
        setIsScrollable(scrollableRef.current.scrollWidth > scrollableRef.current.clientWidth)
      }
    }

    updateScrollable()

    window.addEventListener('resize', updateScrollable)

    return () => {
      window.removeEventListener('resize', updateScrollable)
    }
  }, [])

  const handleSort = useCallback(
    (sortedItemsNew: Array<{ id: string; item: Item }>) => {
      const newItems = sortedItemsNew.map(({ item }) => item)

      if (isEqual(items, newItems)) {
        return
      }

      onSort?.(newItems)
    },
    [items, onSort]
  )

  const handleCheckboxClick = useCallback(
    (item: Item, isSelectedNew: boolean) => () => {
      if (isSelectedNew) {
        onSelect?.([...selectedItems, item])
      } else {
        onSelect?.(selectedItems?.filter(({ id }) => id !== item.id))
      }
    },
    [selectedItems, onSelect]
  )

  const renderRow = useCallback(
    (item: Item, rowIndex: number) => {
      const isSelected = selectedItemsIds.includes(item.id)
      const isActive = activeItem?.id === item.id

      return (
        <div
          key={item.id}
          css={classes.row(isSortable, isActive)}
          className={`table-row ${isActive ? 'active-table-row' : ''}`}
          onClick={() => onItemClick?.(item)}
          data-testid={`table-row-${item.id}`}
        >
          {isSelectable && (
            <Checkbox
              checked={isSelected}
              onClick={handleCheckboxClick(item, !isSelected)}
              css={classes.checkbox}
              className="table-checkbox"
            />
          )}
          {columns.map(({ renderCell, flex }, index) => (
            <div key={index} css={classes.cell(flex)} className="table-cell" data-testid={`table-cell-${index}`}>
              {renderCell && renderCell(item, rowIndex)}
            </div>
          ))}
          {isSortable && <DnDIcon className="table-dnd-icon" css={classes.dndSpace} data-testid="table-dnd-icon" />}
        </div>
      )
    },
    [columns, selectedItemsIds]
  )

  return (
    <div
      css={classes.scrollableWrapper(wrapperPadding)}
      ref={scrollableRef}
      className={['table-wrapper', isScrollable && 'scrollable', wrapperClassName].filter(Boolean).join(' ')}
      {...props}
    >
      <div css={classes.wrapper(wrapperMinWidth)} className={className}>
        {!hideHeader && (
          <div css={classes.header} className="table-header" data-testid="table-header">
            {isSelectable && (
              <Checkbox
                checked={isAllSelected}
                onClick={() => onSelect?.(isAllSelected ? [] : [...items])}
                css={classes.checkbox}
                className="table-checkbox"
              />
            )}
            {columns.map(({ title, flex, orderField }, index) => (
              <div
                key={index}
                css={[
                  classes.cell(flex),
                  classes.titleCell({
                    available: !!orderField,
                    direction: order?.field === orderField ? order?.direction : null,
                  }),
                ]}
                className="table-cell"
                onClick={orderField ? () => onOrder?.(getNextOrder(orderField, order)) : undefined}
                data-testid={`table-cell-${index}`}
              >
                {title}
                {orderField && <ArrowUpIcon className="table-order-icon" />}
              </div>
            ))}
            {isSortable && <div css={classes.dndSpace} className="table-dnd-space" />}
          </div>
        )}
        {!items.length ? (
          isNotLoaded ? (
            <div css={[classes.message, classes.loader]} className="loader" data-testid="loader">
              <Loader />
            </div>
          ) : (
            <div css={classes.message} className="message" data-testid="nothing-found">
              Nothing Found
            </div>
          )
        ) : isSortable ? (
          <ReactSortable
            list={sortedItems}
            setList={handleSort}
            animation={DND_ANIMATION}
            css={classes.wrapper(wrapperMinWidth)}
            className="table-sortable-list"
          >
            {sortedItems.map(({ item }, index) => renderRow(item, index))}
          </ReactSortable>
        ) : (
          items.map(renderRow)
        )}
      </div>
    </div>
  )
}
